/**
 * The address an entity record hands to an address control, narrowed to the components that
 * control shows. `useAddressSearch` calls it for the search hit, and `useAddressUI.handleSelect`
 * calls it again for the record read on select.
 *
 * The source names one address-typed field, so nothing here decides what is an address: the caller
 * has already resolved that at configuration time. `componentKeys` is the allow-list of components
 * the target control shows, `formatted` is always this control's own derivation over the components
 * it kept, and the result carries no `alternativeData` — a pulled address claims no Places
 * provenance. A hidden printed component is never resurrected through the display string.
 */

import type { AddressComponents, StreetAddress } from '#types';
import { ADDRESS_LOCATION_KEYS, asStreetAddress, formatStreetAddressFromComponents } from '#types';

/**
 * The components `formatStreetAddressFromComponents` prints. `phone` and `email` are not among
 * them, which is why a contact key the control hides cannot leak through the display-string
 * fallback while a hidden recipient line can.
 */
const DISPLAY_STRING_KEYS: readonly (keyof AddressComponents)[] = [
  ...ADDRESS_LOCATION_KEYS,
  'recipientName',
  'company',
];

const filledComponents = (
  components: AddressComponents | undefined,
  componentKeys: readonly (keyof AddressComponents)[]
): AddressComponents =>
  Object.fromEntries(
    componentKeys.flatMap((key) => {
      const value = components?.[key];
      return typeof value === 'string' && value.trim() !== '' ? [[key, value.trim()]] : [];
    })
  );

/**
 * A stored address as the control would author it: the allowed, filled components only, with
 * `formatted` re-derived from them. When that derivation renders nothing — no allowed component is
 * filled, or the only filled ones are contact lines the label never prints — the stored display
 * string joins them as `street`, the same normalization a bare string receives.
 *
 * A *printed* component outside the allow-list closes that door. A recipient-only address — the
 * shape 48 live rows hold — has a display string made entirely of components a recipient-hiding
 * field may not show, so resurrecting it as `street` would put the hidden name back on the label
 * through the back door. A hidden `phone` cannot leak that way, because the display string never
 * carried it. `formatted` is read defensively: `asStreetAddress` passes any object through unchecked.
 */
export const pulledRecordAddress = (
  stored: unknown,
  componentKeys: readonly (keyof AddressComponents)[]
): StreetAddress | undefined => {
  const normalized = asStreetAddress(stored);
  if (!normalized) return undefined;
  const components = filledComponents(normalized.components, componentKeys);
  const formatted = formatStreetAddressFromComponents(components);
  if (formatted !== '') return { formatted, components };

  const withheldPrintedKeys = DISPLAY_STRING_KEYS.filter((key) => !componentKeys.includes(key));
  if (Object.keys(filledComponents(normalized.components, withheldPrintedKeys)).length > 0) {
    return undefined;
  }
  if (!componentKeys.includes('street')) return undefined;

  const display = typeof normalized.formatted === 'string' ? normalized.formatted.trim() : '';
  if (display === '') return undefined;
  const withStreet: AddressComponents = { ...components, street: display };
  return { formatted: formatStreetAddressFromComponents(withStreet), components: withStreet };
};
